export const vehicle = {
  list: {
    title: 'Vehicles',
    addVehicle: 'Add Vehicle',
    searchPlaceholder: 'Search vehicles...',
    loadError: 'Failed to load vehicles.',
    openRecordFor: 'Open the vehicle record for {{vehicle}}',
    result: {
      singular: 'vehicle',
      plural: 'vehicles',
    },
    viewBar: {
      defaultLabel: 'All vehicles',
    },
    columns: {
      imageAlt: 'Image',
      chevronAlt: 'Open',
      licensePlate: 'License Plate',
      status: 'Status',
      location: 'Location',
      drivetrain: 'Drivetrain',
      mileageMeters: 'Mileage',
      currentDriver: 'Driver',
      vehicleAgeYears: 'Vehicle age',
      co2Hint: 'WLTP',
    },
    filters: {
      modelHint: 'Select a brand first to narrow the model list.',
      modelTypeHint: 'Select a model first to narrow the model type list.',
    },
    footnotes: {
      vehicleAge: 'From the production date',
    },
    stats: {
      activeInFleet: 'Active in the fleet',
      averageAge: 'Ø vehicle age',
      highestMileage: 'Highest mileage',
      electricShare: 'Electric share',
      years: '{{count}} yr',
      years_other: '{{count}} yrs',
      loadError: 'Failed to load metrics.',
    },
    empty: {
      title: 'No vehicles yet',
      description: 'Add your first vehicle to start building the fleet.',
    },
    noResults: {
      title: 'No vehicles found',
      description: 'Adjust your search or filters to see results again.',
      reset: 'Reset filters',
    },
  },

  // Detail-record field labels, keyed by the backend attribute name.
  attributes: {
    brand: 'Make',
    model: 'Model',
    modelType: 'Model type',
    vin: 'VIN',
    drivetrain: 'Drivetrain',
    transmission: 'Transmission',
    color: 'Exterior Colour',
    imageUrl: 'Photo',
    mileageMeters: 'Mileage',
    mileageRecordedOn: 'Mileage Recorded On',
    firstRegistration: 'First Registration',
    createdAt: 'Created',
    createdBy: 'Created By',
    updatedAt: 'Last Updated',
    updatedBy: 'Updated By',
    status: 'Status',
    licensePlate: 'Licence Plate',
    countryCode: 'Country',
    currentDriver: 'Current Driver',
    costCenter: 'Cost Center',
    hsn: 'HSN',
    tsn: 'TSN',
    productionDate: 'Production Date',
    bodyType: 'Body Type',
    doors: 'Doors',
    seats: 'Seats',
    vehicleClass: 'Vehicle Class',
    // Derived from `drivetrain` (data-model doc §4) — never editable.
    primaryEnergy: 'Primary Energy',
    secondaryEnergy: 'Secondary Energy',
    secondaryDrive: 'Secondary Drive',
    highVoltageSystem: 'High-Voltage System',
    // Drivetrain & engine / High voltage & charging / Consumption & range /
    // Dimensions & weights / Trailer & loads / Load compartment
    // (manage-vehicles-05).
    driveLayout: 'Drive Layout',
    systemPowerWatts: 'System Power',
    enginePowerWatts: 'Engine Power',
    displacementCm3: 'Displacement',
    acChargingPort: 'AC Charging Port',
    maxAcChargingPowerWatts: 'Max. AC Charging Power',
    dcChargingPort: 'DC Charging Port',
    maxDcChargingPowerWatts: 'Max. DC Charging Power',
    testCycle: 'Test Cycle',
    co2CombinedMgKm: 'CO₂ Combined',
    emissionClass: 'Emission Class',
    depletedBatteryConsumptionMl: 'Consumption (Depleted Battery)',
    allElectricConsumptionWh: 'All-Electric Consumption',
    electricRangeMeters: 'Electric Range',
    heatPump: 'Heat Pump',
    adBlueSystemPresent: 'AdBlue System',
    adBlueTankMl: 'AdBlue Tank Capacity',
    lengthMinMm: 'Length (min.)',
    lengthMaxMm: 'Length (max.)',
    widthWithoutMirrorsMinMm: 'Width without Mirrors (min.)',
    widthWithoutMirrorsMaxMm: 'Width without Mirrors (max.)',
    widthIncludingMirrorsMm: 'Width incl. Mirrors',
    heightMinMm: 'Height (min.)',
    heightMaxMm: 'Height (max.)',
    kerbWeightMinGrams: 'Kerb Weight (min.)',
    kerbWeightMaxGrams: 'Kerb Weight (max.)',
    grossMassGrams: 'Permissible Gross Mass',
    payloadMinGrams: 'Payload (min.)',
    payloadMaxGrams: 'Payload (max.)',
    towingCapacityBrakedGrams: 'Towing Capacity, Braked',
    towingCapacityUnbrakedGrams: 'Towing Capacity, Unbraked',
    grossCombinationMassGrams: 'Permissible Gross Combination Mass',
    noseWeightGrams: 'Nose Weight',
    roofLoadGrams: 'Roof Load',
    bootNormalMl: 'Boot, Normal',
    bootMaxMl: 'Boot, Maximum',
    frunkMl: 'Frunk',
    // Derived (`computeTotalRangeMeters`, manage-vehicles-07, frontend-only) —
    // never editable, never counted.
    totalRangeMeters: 'Total range',
  },

  // Storage slot names — the container a fuel/energy carrier is held in,
  // shown on the Energy storage card (manage-vehicles-07, PRD copy table).
  // Distinct from `energyCarrier` below: the consumption entries are
  // labelled by energy, the storage slots by container.
  storageContainer: {
    petrol: 'Petrol tank',
    diesel: 'Diesel tank',
    lpg: 'LPG tank',
    cng: 'CNG storage',
    lng: 'LNG storage',
    ethanol: 'Ethanol tank',
    electricity: 'Battery',
  },

  // Keys are the backend vocabulary and stay as lookup keys; only the labels shown are translated.
  status: {
    active: 'Active',
    inactive: 'Inactive',
  },

  // Antriebsart. Keys are the backend vocabulary; labels are the display terms.
  drivetrain: {
    petrol: 'Petrol',
    diesel: 'Diesel',
    petrolMildHybrid: 'Petrol mild hybrid',
    dieselMildHybrid: 'Diesel mild hybrid',
    petrolFullHybrid: 'Petrol full hybrid',
    dieselFullHybrid: 'Diesel full hybrid',
    plugInHybridPetrol: 'Plug-in hybrid petrol',
    plugInHybridDiesel: 'Plug-in hybrid diesel',
    batteryElectricBev: 'Battery electric (BEV)',
    lpg: 'LPG',
    cng: 'CNG',
  },

  // Karosserieform. Keys are the backend vocabulary (`BodyType`).
  bodyType: {
    saloon: 'Saloon',
    estate: 'Estate',
    hatchback: 'Hatchback',
    suv: 'SUV',
    coupe: 'Coupé',
    convertible: 'Convertible',
    roadster: 'Roadster',
    van: 'Van',
    minibus: 'Minibus',
    panelVan: 'Panel van',
    pickUp: 'Pick-up',
    other: 'Other',
  },

  // Fahrzeugklasse. The code is stored; this is the plain-language gloss
  // shown beside it (data-model doc §3, Group 2 · Vehicle).
  vehicleClass: {
    M1: 'Passenger car',
    M1G: 'Off-road passenger car',
    N1: 'Van / light truck up to 3.5 t',
    N1G: 'Off-road van up to 3.5 t',
    N2: 'Truck 3.5–12 t',
    N2G: 'Off-road truck 3.5–12 t',
    N3: 'Truck over 12 t',
    N3G: 'Heavy off-road truck over 12 t',
    M2: 'Minibus / bus up to 5 t',
    M2G: 'Off-road minibus',
    M3: 'Bus over 5 t',
    M3G: 'Off-road bus',
  },

  // Energiequelle. Keys are the backend vocabulary (`EnergyCarrier`).
  energyCarrier: {
    petrol: 'Petrol',
    diesel: 'Diesel',
    lpg: 'LPG',
    cng: 'CNG',
    lng: 'LNG',
    ethanol: 'Ethanol',
    electricity: 'Electricity',
  },

  boolean: {
    yes: 'Yes',
    no: 'No',
  },

  driveLayout: {
    front: 'Front',
    rear: 'Rear',
    allWheel: 'All-wheel',
  },

  acChargingPort: {
    'Type 2': 'Type 2',
    'Type 1': 'Type 1',
    none: 'None',
  },

  dcChargingPort: {
    CCS: 'CCS',
    CHAdeMO: 'CHAdeMO',
    none: 'None',
  },

  testCycle: {
    WLTP: 'WLTP',
    NEDC: 'NEDC',
  },

  // The tenant's grant-side relationship to the vehicle (`GrantRoleBadge`) — a
  // vehicle held under both ownership and custody shows both badges, never a
  // synthesized "owner and keeper" value.
  grantRole: {
    viewer: 'Viewer',
    owner: 'Owner',
    keeper: 'Keeper',
  },

  // Registration country (data-model doc §2.5) — drives HSN/TSN and EU/EEA
  // visibility and plate uniqueness. Same country set as `licensePlate.country`
  // below, keyed here under the raw attribute so `VehicleFieldEditor`'s
  // generic `vehicle:{{attribute}}.{{option}}` select-option lookup resolves it.
  countryCode: {
    DE: 'Germany',
    FR: 'France',
    NL: 'Netherlands',
    CH: 'Switzerland',
  },

  // Display-only hp equivalent beside the stored kW figure (issue AC).
  power: {
    withHp: '{{kw}} kW ({{hp}} hp)',
  },

  access: {
    noAccess: 'No access',
    hiddenByGrant: {
      label: 'no access (grant)',
      tooltip: 'Not accessible: tenant lacks the required grant',
    },
    hiddenByRole: {
      label: 'no access (role)',
      tooltip: 'Your role does not grant access to this attribute group',
    },
  },

  licensePlate: {
    aria: 'License plate {{plate}} ({{country}})',
    ariaNoCountry: 'License plate {{plate}}',
    missing: 'No licence plate',
    country: {
      DE: 'Germany',
      FR: 'France',
      NL: 'Netherlands',
      CH: 'Switzerland',
    },
  },

  // The four-step create assistant (manage-vehicles-14, PRD "Create a vehicle").
  wizard: {
    page: {
      title: 'Add Vehicle',
      noPermission: 'You do not have permission to create vehicles in this tenant.',
    },
    toast: {
      created: 'Vehicle added',
      createFailed: 'The vehicle could not be created.',
    },
    nav: {
      stepCount: 'Step {{current}} of {{total}}',
      next: 'Next',
      back: 'Back',
      cancel: 'Cancel',
      create: 'Add vehicle',
      creating: 'Creating…',
    },
    step: {
      operation: { title: 'Operation' },
      identity: { title: 'Identity' },
      vehicle: { title: 'Vehicle' },
      technicalDetails: { title: 'Technical details' },
    },
    // {{count}} technical mandatory field(s) — today always `transmission` alone.
    deferredMandatory:
      '{{count}} technical mandatory field is still missing. You can create the vehicle anyway — add it in the vehicle record at any time.',
    deferredMandatory_other:
      '{{count}} technical mandatory fields are still missing. You can create the vehicle anyway — add them in the vehicle record at any time.',
    drivetrainMissing: 'Please choose the drivetrain first.',
    unsavedChanges: {
      title: 'Discard this vehicle?',
      description: 'What you entered has not been saved.',
      discard: 'Discard',
      keepEditing: 'Keep editing',
    },
  },

  detail: {
    fallbackTitle: 'Vehicle Detail',
    back: 'All Vehicles',
    loadError: 'Failed to load vehicle.',
    empty: 'No details available for this vehicle.',
    actions: {
      edit: 'Edit',
      activate: 'Activate vehicle',
      deactivate: 'Deactivate vehicle',
    },
    toast: {
      updated: 'Vehicle updated',
      updateFailed: 'The vehicle could not be saved. Please try again.',
      activated: 'Vehicle activated',
      activateFailed: 'The vehicle could not be activated. Please try again.',
      deactivated: 'Vehicle deactivated',
      deactivateFailed: 'The vehicle could not be deactivated. Please try again.',
    },
    // Status is an action, not a field (manage-vehicles-11): Deactivate raises
    // this confirmation beside the badge; Activate applies immediately.
    deactivateConfirm: {
      title: 'Deactivate {{vehicle}}?',
      description: 'Inactive vehicles leave the booking pool immediately.',
      endAssignment: {
        label: 'End the running driver assignment',
        description: 'The driver becomes available for other vehicles.',
      },
      cancel: 'Cancel',
      confirm: 'Deactivate',
      deactivating: 'Deactivating…',
    },
    requiredMarker: 'required',
    // Accessible name for the icon beside an error/warning message (WCAG
    // 1.4.1 — severity is not colour alone).
    findingIcon: {
      error: 'Error',
      warning: 'Warning',
    },
    // The Vehicle card's Make → Model → Model type narrowing selects (issue AC).
    modelPath: {
      chooseMakeFirst: 'Choose a make first',
      chooseModelFirst: 'Choose a model first',
    },
    blockedSave: {
      // {{tab}} is the visible tab name (vehicle:detail.tabs.*) — the PRD's
      // 'Open finding under "{tab}"' line beside a disabled Save.
      otherTab: 'Open finding under "{{tab}}"',
    },
    unsavedChanges: {
      title: 'Discard your changes?',
      description: 'Your changes to this vehicle have not been saved.',
      discard: 'Discard',
      keepEditing: 'Keep editing',
    },
    // Frontend-only (PRD: "warnings are entirely frontend-computed"), never
    // block — the error-table equivalents share their codes with the backend
    // and resolve through `common:fieldErrors.<code>` instead.
    warnings: {
      mileageUnusuallyHigh: 'Unusually high — over 2,000,000 km. Please check.',
      mileageBelowLastReading: 'Lower than the last recorded reading.',
      powerUnusuallyHigh: 'Unusually high for a fleet vehicle. Please check.',
      displacementUnusuallyLarge: 'Unusually large. Please check.',
      electricVehicleMissingConsumption: 'An electric vehicle should have its electricity consumption recorded.',
      emissionClassNotRecorded: 'Emission class has not been recorded yet.',
      productionDateAfterFirstRegistration: 'Lies after the first registration.',
      combinationMassBelowGrossMass: "Below the vehicle's own permissible gross mass.",
      widthWithMirrorsBelowWithout: 'A vehicle is not narrower with mirrors than without.',
      bootMaxBelowNormal: 'The maximum volume is below the normal one.',
      noWheelTireCombination: 'No wheel/tire combination has been recorded yet.',
    },
    tabs: {
      operation: 'Operation',
      masterData: 'Master data',
      driver: 'Driver',
    },
    notFound: {
      title: 'Vehicle not found',
      body: 'No vehicle with the ID {{vehicle}}',
      back: 'Back to the list',
    },
    hero: {
      grantRole: 'Grant role',
      orgUnit: 'Org unit',
    },
    sections: {
      operation: { title: 'Operation' },
      identification: { title: 'Identification' },
      vehicle: { title: 'Vehicle' },
      drivetrainEngine: { title: 'Drivetrain & Engine' },
      energyStorage: { title: 'Energy Storage' },
      highVoltageCharging: { title: 'High Voltage & Charging' },
      consumptionRange: { title: 'Consumption & Range' },
      dimensionsWeights: { title: 'Dimensions & Weights' },
      trailerLoads: { title: 'Trailer & Loads' },
      loadCompartment: { title: 'Load Compartment' },
      wheelsTires: { title: 'Wheels & Tires' },
      hasError: 'Contains an error',
      hasWarning: 'Contains a warning',
    },
    // The two drivetrain-derived collections' explanatory line, shown in
    // place of any input until a drivetrain is chosen (manage-vehicles-07,
    // PRD copy table).
    energyStorage: {
      noDrivetrain: 'Which storage exists is only settled once the drivetrain is chosen.',
    },
    consumptionRange: {
      noDrivetrain: 'Which energy carriers exist is only settled once the drivetrain is chosen.',
    },
    audit: {
      created: 'Created {{date}}',
      createdBy: 'Created {{date}} by {{user}}',
      updated: 'last updated {{date}}',
      updatedBy: 'last updated {{date}} by {{user}}',
    },
  },

  // The Wheels & Tires section (manage-vehicles-09) — a free list, unlike
  // the drivetrain-derived collections above, so it is not keyed by carrier.
  wheelTire: {
    noCombination: 'No combination recorded.',
    add: 'Add combination',
    remove: 'Remove',
    defaultAxlePosition: 'Axle 1+2',
    columns: {
      axlePosition: 'Axle',
      tireSize: 'Tire size',
      loadSpeed: 'Load/Speed',
      loadIndex: 'Load index',
      speedIndex: 'Speed index',
      markings: 'Markings',
      rimSize: 'Rim',
      offsetEtMm: 'ET (mm)',
    },
  },

  form: {
    required: 'Required',
    cancel: 'Cancel',
    selectPlaceholder: '—',
    save: 'Save',
    saving: 'Saving…',
  },

  // The Driver tab's Current-assignment card (issue 12) — the vehicle-side
  // mirror of driver:assignment, driving the same driver-pod commands from
  // the other side of the assignment.
  driverAssignment: {
    title: 'Current assignment',
    empty: 'No driver currently assigned',
    assign: 'Assign driver',
    end: 'End assignment',
    inactiveVehicleReason: 'Inactive vehicles cannot be assigned a driver.',
    columns: { driver: 'Driver', date: 'Assignment date' },
    dialog: {
      assign: {
        title: 'Assign a driver',
        driverLabel: 'Driver',
        driverPlaceholder: 'Select a driver…',
        driverLoading: 'Loading drivers…',
        noDriverAvailable: 'No unassigned driver available',
        startLabel: 'Start date',
        cancel: 'Cancel',
        confirm: 'Assign driver',
        assigning: 'Assigning…',
      },
      end: {
        title: 'End assignment for {{driver}}?',
        description: 'The driver becomes available for other vehicles.',
        cancel: 'Cancel',
        confirm: 'End assignment',
        ending: 'Ending…',
      },
    },
    toast: {
      assigned: 'Driver assigned',
      ended: 'Assignment ended',
      failed: 'Assignment could not be saved. Please try again.',
    },
  },
};

export type VehicleResources = typeof vehicle;
